import { useEffect, useRef, useState } from "react";
import { ArrowRight, Users, Package, Briefcase, Wrench } from "lucide-react";
import { useNavigate } from "react-router-dom";

const CYCLE_MS = 3000;

const PROFILES = [
  {
    icon: Package,
    color: "from-blue-500 to-blue-600",
    borderActive: "rgba(59,130,246,0.4)",
    glow: "rgba(59,130,246,0.12)",
    tag: "Manufacturer",
    tagActiveColor: "#1d4ed8",
    headline: "You make the battery.",
    sub: "We handle the passport.",
    desc: "Your engineers shouldn't be decoding Annex XIII - they should be building cells. Provinyx translates EU Battery Regulation requirements into a guided data collection flow your team can complete in days, not months.",
  },
  {
    icon: Briefcase,
    color: "from-blue-500 to-blue-600",
    borderActive: "rgba(59,130,246,0.4)",
    glow: "rgba(59,130,246,0.12)",
    tag: "Importer / Brand",
    tagActiveColor: "#1d4ed8",
    headline: "You place batteries on the EU market.",
    sub: "You're the Responsible Economic Operator.",
    desc: "Whether you import from Asia or white-label from a contract manufacturer, you bear the compliance obligation. Provinyx lets you manage supplier passports under your own brand and submit them to the EU registry.",
  },
  {
    icon: Wrench,
    color: "from-blue-500 to-blue-600",
    borderActive: "rgba(59,130,246,0.4)",
    glow: "rgba(59,130,246,0.12)",
    tag: "Recycler / Repair",
    tagActiveColor: "#1d4ed8",
    headline: "You give batteries a second life.",
    sub: "The passport travels with the battery.",
    desc: "Update the State of Health at second-life entry. Log repair events. Record end-of-life routing. The regulation requires all operators in the chain to keep the passport current - Provinyx makes that a 30-second scan, not a compliance project.",
  },
];

const STATS = [
  { value: "Feb 2027", label: "Mandatory deadline" },
  { value: "≥ 2 kWh", label: "Battery size threshold" },
  { value: "7", label: "Annex XIII data categories" },
  { value: "4%", label: "Max turnover fine" },
];

export default function MarketWeServe() {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [activeProfile, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const ob = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold: 0.08 }
    );
    if (sectionRef.current) ob.observe(sectionRef.current);
    return () => ob.disconnect();
  }, []);

  // Timed carousel: open one card at a time, move on after 3s.
  // Re-runs when the active card changes (so a manual click restarts the timer)
  // and stops while the pointer is over the cards, so the button is easy to click.
  useEffect(() => {
    if (!visible || paused) return;
    const t = setTimeout(
      () => setActive(p => (p + 1) % PROFILES.length),
      CYCLE_MS
    );
    return () => clearTimeout(t);
  }, [visible, paused, activeProfile]);

  return (
    <section
      ref={sectionRef}
      className="relative py-24 bg-slate-50 overflow-hidden"
    >
      {/* Subtle grid texture */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.025]"
        style={{
          backgroundImage: "linear-gradient(rgba(59,130,246,1) 1px, transparent 1px), linear-gradient(90deg, rgba(59,130,246,1) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />
      <div className="absolute top-0 left-0 right-0 h-px bg-slate-200" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-slate-200" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Heading */}
        <div
          className="text-center mb-16"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(30px)",
            transition: "opacity 0.7s ease, transform 0.7s ease",
          }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-200 text-blue-600 text-xs font-semibold uppercase tracking-widest mb-6">
            <Users className="w-3.5 h-3.5" />
            The Market We Serve
          </div>
          <h2 className="text-4xl sm:text-5xl font-extrabold text-slate-900 leading-tight mb-5">
            The deadline treats everyone equally.{" "}
            <span className="text-blue-600">
              The support doesn't.
            </span>
          </h2>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto">
            Large OEMs have compliance departments. SMEs have a spreadsheet and a deadline.
            Provinyx was built for the battery businesses that need to be compliant but
            can't afford a six-month consulting project to get there.
          </p>
        </div>

        {/* Stats strip */}
        <div
          className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-16"
          style={{
            opacity: visible ? 1 : 0,
            transition: "opacity 0.7s ease 0.15s",
          }}
        >
          {STATS.map(({ value, label }, i) => (
            <div
              key={label}
              className="text-center p-5 rounded-2xl border bg-white"
              style={{
                borderColor: "#e2e8f0",
                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(20px)",
                transition: `opacity 0.6s ease ${0.15 + i * 0.1}s, transform 0.6s ease ${0.15 + i * 0.1}s`,
              }}
            >
              <div className="text-2xl sm:text-3xl font-black text-slate-900 mb-1">{value}</div>
              <div className="text-xs text-slate-500">{label}</div>
            </div>
          ))}
        </div>

        {/* Timed carousel cards: the active card drops down to reveal its explanation */}
        <div
          className="grid lg:grid-cols-3 gap-4 items-start"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          {PROFILES.map((p, i) => {
            const Icon = p.icon;
            const isActive = i === activeProfile;
            return (
              <div
                key={p.tag}
                role="button"
                tabIndex={0}
                aria-expanded={isActive}
                onClick={() => setActive(i)}
                onKeyDown={e => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setActive(i);
                  }
                }}
                className="text-left p-5 rounded-2xl border cursor-pointer bg-white"
                style={{
                  opacity: visible ? 1 : 0,
                  transform: visible ? "translateY(0)" : "translateY(30px)",
                  transition: `opacity 0.6s ease ${0.2 + i * 0.1}s, transform 0.6s ease ${0.2 + i * 0.1}s, border-color 0.3s ease, box-shadow 0.3s ease`,
                  borderColor: isActive ? p.borderActive : "#e2e8f0",
                  boxShadow: isActive ? `0 8px 28px ${p.glow}` : "0 1px 3px rgba(0,0,0,0.06)",
                }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center bg-gradient-to-br ${p.color}`}>
                    <Icon className="text-white" style={{ width: 18, height: 18 }} />
                  </div>
                  <span
                    className="text-xs font-semibold px-2.5 py-1 rounded-full transition-colors duration-300"
                    style={{
                      background: isActive ? p.glow : "#f1f5f9",
                      color: isActive ? p.tagActiveColor : "#64748b",
                    }}
                  >
                    {p.tag}
                  </span>
                </div>
                <div className="font-bold text-slate-900 text-base">{p.headline}</div>
                <div className="text-slate-500 text-sm mt-1">{p.sub}</div>

                {/* Drop-down explanation */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateRows: isActive ? "1fr" : "0fr",
                    opacity: isActive ? 1 : 0,
                    transition: "grid-template-rows 0.5s ease, opacity 0.4s ease",
                  }}
                >
                  <div className="overflow-hidden">
                    <p className="text-slate-600 text-base leading-relaxed mt-4 pt-4 border-t border-slate-100">
                      {p.desc}
                    </p>
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        navigate("/battery-passport");
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                      tabIndex={isActive ? 0 : -1}
                      className="group mt-5 mb-1 flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white transition-all duration-300"
                      style={{
                        background: "linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%)",
                        boxShadow: "0 4px 15px rgba(37,99,235,0.35)",
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.boxShadow = "0 8px 25px rgba(37,99,235,0.55)";
                        e.currentTarget.style.transform = "translateY(-1px)";
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.boxShadow = "0 4px 15px rgba(37,99,235,0.35)";
                        e.currentTarget.style.transform = "translateY(0)";
                      }}
                    >
                      See how it works
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                    </button>
                  </div>
                </div>

                {/* 3-second progress bar */}
                <div className="mt-4 h-0.5 bg-slate-100 rounded-full overflow-hidden">
                  {isActive && (
                    <div
                      key={`${activeProfile}-${paused}`}
                      className="h-full rounded-full bg-blue-500"
                      style={
                        paused
                          ? { width: "100%" } // held while hovering; timer restarts on leave
                          : { animation: `mwsProgress ${CYCLE_MS}ms linear forwards` }
                      }
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        @keyframes mwsProgress {
          from { width: 0%; }
          to   { width: 100%; }
        }
      `}</style>
    </section>
  );
}